import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  Scale,
  FileText,
  BriefcaseBusiness,
  CheckCircle2,
  ShieldCheck,
  CreditCard,
  CalendarClock,
  CircleHelp,
  Mail,
  ArrowUpRight,
  ChevronRight,
  Ban,
  Handshake,
  Globe,
} from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const C = {
  bg: "#080d13",
  panel: "rgba(17, 25, 35, 0.84)",
  border: "rgba(163, 230, 53, 0.18)",
  green: "#a3e635",
  greenSoft: "rgba(163, 230, 53, 0.10)",
  text: "#edf2f7",
  muted: "#a2adba",
  faint: "#6f7c8b",
};

const termsSections = [
  {
    id: "acceptance",
    number: "01",
    icon: CheckCircle2,
    title: "Acceptance of These Terms",
    content: (
      <>
        <p>These Terms and Conditions govern your access to and use of the Beyond the Analysis website and any services we make available through it. By using our website or engaging our services, you agree to these terms.</p>
        <p>If you do not agree to these terms, please do not use the website or proceed with a service engagement. If you are accepting these terms on behalf of a business, you confirm that you have authority to do so.</p>
      </>
    ),
  },
  {
    id: "services",
    number: "02",
    icon: BriefcaseBusiness,
    title: "Our Services",
    content: (
      <>
        <p>Beyond the Analysis may provide digital marketing, advertising support, website development, strategy, analytics, optimisation, automation, and related business services.</p>
        <p>The exact scope, deliverables, timeline, fees, and responsibilities for a project should be confirmed in a written proposal, statement of work, order, or other written agreement between you and us.</p>
        <p>Unless expressly agreed in writing, discussions on this website are informational and do not constitute a guarantee that a particular service or result will be provided.</p>
      </>
    ),
  },
  {
    id: "client-responsibilities",
    number: "03",
    icon: Handshake,
    title: "Client Responsibilities",
    content: (
      <>
        <p>To help us deliver agreed services, you agree to:</p>
        <ul>
          <li>Provide accurate information, timely feedback, and necessary approvals.</li>
          <li>Ensure that you have the rights and permissions to use materials, images, brand assets, data, and content you provide.</li>
          <li>Provide access to relevant accounts or platforms only where needed and through reasonably secure methods.</li>
          <li>Review deliverables and raise concerns within any review period agreed in writing.</li>
          <li>Comply with laws and third-party platform policies applicable to your business and campaigns.</li>
        </ul>
        <p>Delays in receiving information, access, approvals, or payment may affect project schedules and delivery dates.</p>
      </>
    ),
  },
  {
    id: "fees",
    number: "04",
    icon: CreditCard,
    title: "Fees, Payments & Expenses",
    content: (
      <>
        <p>Fees, billing schedules, payment deadlines, and any applicable taxes will be set out in the relevant proposal, invoice, or written agreement.</p>
        <ul>
          <li>You agree to pay undisputed invoices by their stated due dates.</li>
          <li>Third-party costs, including advertising spend, software subscriptions, hosting, stock assets, and platform fees, are separate unless expressly included in the agreed fee.</li>
          <li>Work may be paused after reasonable notice if an invoice remains overdue, subject to the applicable agreement and law.</li>
          <li>Any deposits, milestone payments, cancellation charges, or refund terms will be governed by the written agreement and applicable law.</li>
        </ul>
        <p>Do not send payment card details or sensitive credentials through an unsecured message or contact form.</p>
      </>
    ),
  },
  {
    id: "marketing-results",
    number: "05",
    icon: Globe,
    title: "Marketing Results & Third-Party Platforms",
    content: (
      <>
        <p>Digital marketing results depend on many factors, including market conditions, competition, budget, audience, offer, website performance, tracking accuracy, and third-party platform decisions.</p>
        <p>Unless a specific outcome is expressly guaranteed in a signed written agreement, we do not guarantee particular rankings, traffic, leads, sales, return on advertising spend, revenue, or campaign performance.</p>
        <p>Search engines, social networks, advertising platforms, and other third parties may change their policies, algorithms, prices, features, or access requirements. We are not responsible for changes or outages outside our reasonable control.</p>
      </>
    ),
  },
  {
    id: "intellectual-property",
    number: "06",
    icon: FileText,
    title: "Intellectual Property",
    content: (
      <>
        <p>Each party retains ownership of intellectual property it owned before a project began. You are responsible for ensuring that materials you provide may lawfully be used for the agreed purpose.</p>
        <p>Ownership or licensing of project-specific deliverables, source files, designs, copy, code, and other work product will be set out in the relevant written agreement. Where no specific terms are agreed, no transfer of ownership should be assumed beyond rights that apply under law.</p>
        <p>We may use third-party tools, libraries, stock assets, or platform features subject to their own licence terms. We will not disclose your confidential information in portfolio materials without permission, except as permitted by the applicable agreement or law.</p>
      </>
    ),
  },
  {
    id: "acceptable-use",
    number: "07",
    icon: Ban,
    title: "Acceptable Use",
    content: (
      <>
        <p>You must not use our website or services to:</p>
        <ul>
          <li>Break applicable laws or infringe another person's rights.</li>
          <li>Transmit malicious code, attempt unauthorised access, or disrupt website operations.</li>
          <li>Submit false, misleading, fraudulent, or unlawfully obtained information.</li>
          <li>Use content, campaigns, or materials in a way that violates applicable advertising rules or platform policies.</li>
          <li>Misrepresent your identity or authority when communicating with us.</li>
        </ul>
        <p>We may take reasonable steps to protect our website, team, clients, and systems from misuse, consistent with applicable law.</p>
      </>
    ),
  },
  {
    id: "confidentiality",
    number: "08",
    icon: ShieldCheck,
    title: "Confidentiality & Data",
    content: (
      <>
        <p>Each party should take reasonable care to protect non-public business, technical, and project information received from the other party and use it only for the relevant purpose, subject to any written confidentiality agreement and applicable law.</p>
        <p>Our handling of personal information is described in our <Link to="/privacy-policy" style={{ color: C.green, textDecoration: "none" }}>Privacy Policy</Link>. Please avoid sending passwords, payment details, or other highly sensitive information through public website forms.</p>
      </>
    ),
  },
  {
    id: "cancellations",
    number: "09",
    icon: CalendarClock,
    title: "Changes, Suspension & Cancellation",
    content: (
      <>
        <p>Project changes, pauses, or cancellations should be requested in writing. The effect of a change on scope, fees, deadlines, completed work, and third-party costs will be handled under the relevant written agreement.</p>
        <p>Where no separate agreement applies, both parties should communicate promptly and in good faith to resolve outstanding work, invoices, access, and handover arrangements. Any cancellation or refund will remain subject to applicable law.</p>
        <p>We may suspend access to the website or a service where reasonably necessary for security, suspected misuse, non-payment, legal compliance, or circumstances outside our reasonable control.</p>
      </>
    ),
  },
  {
    id: "disclaimers",
    number: "10",
    icon: Scale,
    title: "Disclaimers & Limitation of Liability",
    content: (
      <>
        <p>Our website is provided for general information and may be updated or changed without notice. To the extent permitted by law, we do not warrant that the website will always be uninterrupted, error-free, or free of harmful components.</p>
        <p>To the extent permitted by applicable law, neither party will be liable to the other for indirect or consequential losses arising from a service engagement, subject to the terms of any signed agreement. Nothing in these terms excludes or limits liability that cannot lawfully be excluded or limited.</p>
        <p>Any liability limits, exclusions, or remedies specific to a project should be stated in the applicable written agreement and interpreted under the law that applies.</p>
      </>
    ),
  },
  {
    id: "external-links",
    number: "11",
    icon: ArrowUpRight,
    title: "Third-Party Websites & Tools",
    content: (
      <>
        <p>Our website or services may link to or rely on third-party websites, software, platforms, and tools. These are generally governed by their own terms and privacy policies.</p>
        <p>We do not control independent third-party services and are not responsible for their content, availability, or policies. Your use of those services is subject to your relationship with the relevant provider.</p>
      </>
    ),
  },
  {
    id: "changes-law",
    number: "12",
    icon: CircleHelp,
    title: "Changes to These Terms",
    content: (
      <>
        <p>We may revise these Terms and Conditions from time to time. The revised version will be posted on this page with an updated revision date.</p>
        <p>Changes will apply as permitted by applicable law. Where a separate written agreement governs a service, that agreement will continue to apply to the extent of any inconsistency.</p>
      </>
    ),
  },
];

const TermsAndConditions = () => {
  const pageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".terms-hero-content > *",
        { opacity: 0, y: 26 },
        { opacity: 1, y: 0, duration: 0.75, stagger: 0.1, ease: "power3.out" }
      );

      gsap.utils.toArray(".terms-section, .terms-intro-card, .terms-contact-card").forEach((section) => {
        gsap.fromTo(
          section,
          { opacity: 0, y: 22 },
          {
            opacity: 1,
            y: 0,
            duration: 0.6,
            ease: "power2.out",
            scrollTrigger: { trigger: section, start: "top 88%", once: true },
          }
        );
      });

      gsap.to(".terms-orb-one", {
        y: -24, x: 14, duration: 5, repeat: -1, yoyo: true, ease: "sine.inOut",
      });
      gsap.to(".terms-orb-two", {
        y: 20, x: -12, duration: 6, repeat: -1, yoyo: true, ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  const styles = {
    page: {
      position: "relative",
      minHeight: "100vh",
      overflow: "hidden",
      background: C.bg,
      color: C.text,
      fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      padding: "0 24px 80px",
      isolation: "isolate",
    },
    background: {
      position: "absolute",
      inset: 0,
      zIndex: -1,
      pointerEvents: "none",
      backgroundImage: "linear-gradient(rgba(163,230,53,.035) 1px, transparent 1px), linear-gradient(90deg, rgba(163,230,53,.035) 1px, transparent 1px)",
      backgroundSize: "58px 58px",
      maskImage: "linear-gradient(to bottom, black, transparent 78%)",
    },
    orb: {
      position: "absolute",
      width: 300,
      height: 300,
      borderRadius: "50%",
      filter: "blur(85px)",
      opacity: 0.14,
      background: C.green,
    },
    hero: {
      maxWidth: 1180,
      minHeight: 430,
      margin: "0 auto",
      padding: "100px 0 70px",
      display: "grid",
      gridTemplateColumns: "1.1fr .9fr",
      gap: 48,
      alignItems: "center",
    },
    eyebrow: {
      display: "inline-flex",
      alignItems: "center",
      gap: 9,
      color: C.green,
      fontSize: 12,
      fontWeight: 800,
      letterSpacing: "0.16em",
      marginBottom: 22,
    },
    h1: {
      fontSize: "clamp(42px, 6vw, 76px)",
      lineHeight: 1.02,
      letterSpacing: "-0.055em",
      margin: "0 0 22px",
      fontWeight: 800,
    },
    lead: {
      maxWidth: 630,
      color: C.muted,
      fontSize: 17,
      lineHeight: 1.8,
      margin: "0 0 24px",
    },
    meta: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: 14,
      color: C.muted,
      fontSize: 12,
    },
    metaItem: { display: "inline-flex", alignItems: "center", gap: 7 },
    visual: { minHeight: 310, display: "grid", placeItems: "center", position: "relative" },
    scaleRing: {
      width: 245,
      height: 245,
      borderRadius: "50%",
      border: `1px solid ${C.border}`,
      display: "grid",
      placeItems: "center",
      boxShadow: "0 0 0 22px rgba(163,230,53,.025), 0 0 0 46px rgba(163,230,53,.018), 0 0 80px rgba(163,230,53,.07)",
      color: C.green,
      background: "radial-gradient(circle, rgba(163,230,53,.09), transparent 68%)",
      position: "relative",
    },
    scaleInner: {
      width: 150,
      height: 150,
      borderRadius: 38,
      display: "grid",
      placeItems: "center",
      background: "linear-gradient(145deg, rgba(163,230,53,.12), rgba(17,25,35,.7))",
      border: `1px solid ${C.border}`,
    },
    floatingCard: {
      position: "absolute",
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "13px 16px",
      background: "rgba(17,25,35,.9)",
      border: `1px solid ${C.border}`,
      borderRadius: 13,
      color: C.text,
      fontSize: 12,
      boxShadow: "0 16px 45px rgba(0,0,0,.22)",
      backdropFilter: "blur(14px)",
    },
    layout: {
      maxWidth: 1180,
      margin: "0 auto",
      display: "grid",
      gridTemplateColumns: "260px minmax(0, 1fr)",
      gap: 28,
      alignItems: "start",
    },
    sidebarCard: {
      position: "sticky",
      top: 24,
      padding: 22,
      background: C.panel,
      border: `1px solid ${C.border}`,
      borderRadius: 20,
      backdropFilter: "blur(18px)",
    },
    sidebarLabel: {
      display: "block",
      color: C.faint,
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: "0.16em",
      marginBottom: 16,
    },
    nav: { display: "grid", gap: 4 },
    navLink: {
      display: "grid",
      gridTemplateColumns: "28px 1fr 14px",
      gap: 8,
      alignItems: "center",
      textDecoration: "none",
      color: C.muted,
      padding: "10px 8px",
      borderRadius: 9,
      fontSize: 12,
      transition: "background .2s ease, color .2s ease",
    },
    sidebarNote: {
      display: "flex",
      gap: 10,
      alignItems: "flex-start",
      paddingTop: 18,
      marginTop: 18,
      borderTop: `1px solid ${C.border}`,
      color: C.green,
    },
    card: {
      background: C.panel,
      border: `1px solid ${C.border}`,
      borderRadius: 20,
      padding: "28px 30px",
      marginBottom: 16,
      backdropFilter: "blur(16px)",
      boxShadow: "0 18px 55px rgba(0,0,0,.12)",
    },
    intro: {
      display: "grid",
      gridTemplateColumns: "48px 1fr",
      gap: 17,
      alignItems: "start",
      background: "linear-gradient(120deg, rgba(163,230,53,.075), rgba(17,25,35,.82) 45%)",
    },
    iconBox: {
      width: 46,
      height: 46,
      display: "grid",
      placeItems: "center",
      borderRadius: 13,
      color: C.green,
      background: C.greenSoft,
      border: `1px solid ${C.border}`,
      flexShrink: 0,
    },
    sectionHeading: { display: "flex", alignItems: "center", gap: 15, marginBottom: 22 },
    sectionNumber: {
      display: "block",
      color: C.green,
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: "0.15em",
      marginBottom: 5,
    },
    h2: { margin: 0, fontSize: "clamp(19px, 2vw, 24px)", lineHeight: 1.3, letterSpacing: "-0.025em" },
    body: { color: C.muted, fontSize: 14, lineHeight: 1.85 },
    contact: {
      display: "grid",
      gridTemplateColumns: "50px 1fr",
      gap: 18,
      alignItems: "start",
      background: "linear-gradient(125deg, rgba(163,230,53,.11), rgba(17,25,35,.88) 55%)",
      borderColor: "rgba(163,230,53,.32)",
    },
    contactLink: {
      display: "inline-flex",
      alignItems: "center",
      gap: 7,
      marginTop: 8,
      color: C.green,
      textDecoration: "none",
      fontWeight: 700,
      fontSize: 14,
    },
    footerNote: { display: "flex", gap: 10, alignItems: "flex-start", color: C.faint, fontSize: 12, lineHeight: 1.7, padding: "12px 4px" },
    backLink: { display: "inline-flex", alignItems: "center", gap: 7, marginTop: 20, color: C.muted, textDecoration: "none", fontSize: 13 },
  };

  return (
    <main className="terms-page" ref={pageRef} style={styles.page}>
      <style>{`
        .terms-page * { box-sizing: border-box; }
        .terms-page p { margin: 0 0 14px; }
        .terms-page p:last-child { margin-bottom: 0; }
        .terms-page ul { margin: 8px 0 17px; padding-left: 22px; }
        .terms-page li { padding-left: 4px; margin: 6px 0; }
        .terms-page li::marker { color: ${C.green}; }
        .terms-page strong { color: ${C.text}; font-weight: 700; }
        .terms-page a:focus-visible { outline: 2px solid ${C.green}; outline-offset: 4px; }
        .terms-nav-link:hover { color: ${C.green} !important; background: ${C.greenSoft}; }
        .terms-contact-link:hover { gap: 12px !important; }
        .terms-back-link:hover { color: ${C.green} !important; }
        .terms-ring-dot { position: absolute; width: 9px; height: 9px; border-radius: 50%; background: ${C.green}; box-shadow: 0 0 18px ${C.green}; }
        .terms-ring-dot-one { top: 31px; right: 70px; }
        .terms-ring-dot-two { bottom: 49px; left: 53px; width: 6px; height: 6px; opacity: .7; }
        @media (max-width: 900px) {
          .terms-hero { grid-template-columns: 1fr !important; padding-top: 72px !important; }
          .terms-hero-visual { display: none !important; }
          .terms-layout { grid-template-columns: 1fr !important; }
          .terms-sidebar { display: none !important; }
        }
        @media (max-width: 600px) {
          .terms-page { padding-left: 16px !important; padding-right: 16px !important; padding-bottom: 50px !important; }
          .terms-hero { min-height: 0 !important; padding: 62px 0 38px !important; }
          .terms-lead { font-size: 15px !important; }
          .terms-content-card { padding: 22px 18px !important; border-radius: 16px !important; }
          .terms-section-heading { align-items: flex-start !important; }
          .terms-intro-card, .terms-contact-card { grid-template-columns: 1fr !important; }
          .terms-meta-divider { display: none !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          .terms-page * { scroll-behavior: auto !important; }
        }
      `}</style>

      <div aria-hidden="true" style={styles.background}>
        <div className="terms-orb-one" style={{ ...styles.orb, top: 70, left: -130 }} />
        <div className="terms-orb-two" style={{ ...styles.orb, top: 620, right: -140, width: 250, height: 250, opacity: 0.1 }} />
      </div>

      <section className="terms-hero" style={styles.hero}>
        <div className="terms-hero-content">
          <div style={styles.eyebrow}><Scale size={16} /><span>OUR TERMS, CLEARLY STATED</span></div>
          <h1 style={styles.h1}>Terms that build <span style={{ color: C.green, textShadow: "0 0 36px rgba(163,230,53,.15)" }}>trust.</span></h1>
          <p className="terms-lead" style={styles.lead}>
            These terms explain the basic rules for using the Beyond the Analysis website and working with our team. Please read them before engaging our services.
          </p>
          <div style={styles.meta}>
            <span style={styles.metaItem}><CheckCircle2 size={16} color={C.green} /> Straightforward expectations</span>
            <span className="terms-meta-divider" style={{ width: 1, height: 15, background: C.border }} />
            <span>Last updated: October 6, 2026</span>
          </div>
        </div>

        <div className="terms-hero-visual" aria-hidden="true" style={styles.visual}>
          <div style={styles.scaleRing}>
            <div style={styles.scaleInner}><Scale size={76} strokeWidth={1.2} /></div>
            <span className="terms-ring-dot terms-ring-dot-one" />
            <span className="terms-ring-dot terms-ring-dot-two" />
          </div>
          <div style={{ ...styles.floatingCard, top: 44, right: 0 }}><ShieldCheck size={17} color={C.green} /><span>Clear commitments</span></div>
          <div style={{ ...styles.floatingCard, bottom: 42, left: 0 }}><Handshake size={17} color={C.green} /><span>Built on trust</span></div>
        </div>
      </section>

      <div className="terms-layout" style={styles.layout}>
        <aside className="terms-sidebar">
          <div style={styles.sidebarCard}>
            <span style={styles.sidebarLabel}>ON THIS PAGE</span>
            <nav aria-label="Terms and conditions sections" style={styles.nav}>
              {termsSections.map((section) => (
                <a className="terms-nav-link" key={section.id} href={`#${section.id}`} style={styles.navLink}>
                  <span style={{ color: C.green, fontSize: 10, fontWeight: 800 }}>{section.number}</span>
                  <span>{section.title}</span>
                  <ChevronRight size={14} />
                </a>
              ))}
            </nav>
            <div style={styles.sidebarNote}>
              <ShieldCheck size={19} />
              <p style={{ margin: 0, color: C.muted, fontSize: 12, lineHeight: 1.65 }}>
                Clear expectations help every project run more smoothly.
              </p>
            </div>
          </div>
        </aside>

        <div className="terms-content">
          <section className="terms-intro-card terms-content-card" style={{ ...styles.card, ...styles.intro }}>
            <div style={styles.iconBox}><FileText size={23} /></div>
            <div style={styles.body}>
              <h2 style={{ ...styles.h2, marginBottom: 10, color: C.text }}>A note before you begin</h2>
              <p>These website terms are general information and are not a substitute for a project-specific contract or legal advice. If you hire us, your proposal or signed agreement may include additional terms that apply to your engagement.</p>
            </div>
          </section>

          {termsSections.map((section) => {
            const Icon = section.icon;
            return (
              <section className="terms-section terms-content-card" id={section.id} key={section.id} style={styles.card}>
                <div className="terms-section-heading" style={styles.sectionHeading}>
                  <div style={styles.iconBox}><Icon size={21} /></div>
                  <div>
                    <span style={styles.sectionNumber}>SECTION {section.number}</span>
                    <h2 style={styles.h2}>{section.title}</h2>
                  </div>
                </div>
                <div style={styles.body}>{section.content}</div>
              </section>
            );
          })}

          <section className="terms-contact-card terms-content-card" style={{ ...styles.card, ...styles.contact }}>
            <div style={styles.iconBox}><Mail size={25} /></div>
            <div>
              <span style={styles.sectionNumber}>NEED CLARIFICATION?</span>
              <h2 style={{ ...styles.h2, marginBottom: 12 }}>Questions about these terms?</h2>
              <p style={{ ...styles.body, marginBottom: 8 }}>Contact our team if you have questions about these Terms and Conditions or how they apply to a project.</p>
              <a className="terms-contact-link" href="mailto:YOUR_BUSINESS_EMAIL" style={styles.contactLink}>
                YOUR_BUSINESS_EMAIL <ArrowUpRight size={16} />
              </a>
            </div>
          </section>

          <div style={styles.footerNote}>
            <ShieldCheck size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            <p style={{ margin: 0 }}>Please have these terms reviewed for your business, actual service arrangements, and applicable local laws before publication.</p>
          </div>

          <Link className="terms-back-link" to="/" style={styles.backLink}>
            <ChevronRight size={16} style={{ transform: "rotate(180deg)" }} /> Back to home
          </Link>
        </div>
      </div>
    </main>
  );
};

export default TermsAndConditions;
